import { Settings as LuxonSettings } from 'luxon'
import type { RouteLocationNormalized } from 'vue-router'
import type { QueryParamsByPath, Schemas } from '@/api/types.js'
import type { MergedSettingsData } from '@/models/settings.js'

import {
  buildUserRelationships,
  buildMergedSettingsRelationships,
} from './users.js'
import { useCurrentLocale, useSupportedLocales } from '@/composables/i18next.js'
import { useDisplayNames } from '@/composables/intl.js'
import { useApi } from '@/composables/useApi.js'
import { useTheme } from '@/composables/useTheme.js'
import { keyBy } from '@/utils/array.js'
import { buildIncludedResourceMap } from '@/utils/jsonapi.js'
import type { CurrencyCodes } from '@/utils/money.js'
import { loginUrl } from '@/utils/url.js'

type UserCurrentQueryParams = QueryParamsByPath<
  'get',
  '/{provet_id}/api/users/current'
>

type MergedSettingsQueryParams = QueryParamsByPath<
  'get',
  '/{provet_id}/api/settings/merged'
>

type RouteWithParams = Pick<RouteLocationNormalized, 'params'>

type Identifier = { id: string }
type Department = Schemas['Department']
type DepartmentGroup = Schemas['DepartmentGroup']
type User = Schemas['User']
type Organization = Schemas['Organization']
type SidebarSection = Schemas['SidebarSection']
type Permission = { read?: boolean; write?: boolean }
type ActivePermissions = Record<string, Permission>
type MergedSettings = Schemas['MergedSettings']

const user = ref<User>()
const departments = ref<Department[]>([])
const departmentGroups = ref<DepartmentGroup[]>([])
const activeDepartment = ref<Department>()
const homeDepartment = ref<Department>()
const organization = ref<Organization>()
const permissions = ref<ActivePermissions>()
const navigationSidebar = ref<SidebarSection[]>([])
/**
 * The merged organization & department-level settings in the app.
 * Some department-level settings can override organization-level settings.
 */
const mergedSettings = ref<MergedSettings>()
const clientInteractionLanguage =
  ref<MergedSettingsData['client_interaction_language']>()
const userDefaultLanguage = ref<MergedSettingsData['user_default_language']>()

const channel = useBroadcastChannel<Identifier, Identifier>({
  name: 'provet-cloud-department-switch',
})

watch(channel.data, () => {
  return fetchDepartmentDependentData()
})

watch(user, () => {
  const currentLocale = useCurrentLocale()
  const { convertBackendLocaleToFrontendLocale } = useSupportedLocales()

  if (user.value?.attributes?.language) {
    currentLocale.value = convertBackendLocaleToFrontendLocale(
      user.value?.attributes?.language,
    )
  }
})

// Watch merged settings for theme changes instead of user settings
watch(mergedSettings, () => {
  const { provet_theme, client_interaction_language, user_default_language } =
    mergedSettings.value?.attributes ?? {}
  const provetTheme = provet_theme
  clientInteractionLanguage.value = client_interaction_language
  userDefaultLanguage.value = user_default_language

  if (provetTheme) {
    const { contrastSetting } = useTheme()
    // Note: The "High contrast" theme that a user can select has an "ACCESSIBILITY" value in the DB of Provet
    // and this is what the API returns.
    contrastSetting.value =
      provetTheme === 'ACCESSIBILITY' ? 'increased' : 'default'
  }
})

async function switchDepartment({ id }: Identifier) {
  const api = useApi()
  const route = useRoute()
  const provetId = route.params.provetId

  const response = await api((client) =>
    client.PATCH('/{provet_id}/api/users/current', {
      params: {
        path: { provet_id: provetId },
      },
      body: {
        data: {
          type: 'User',
          id: user.value!.id,
          relationships: {
            active_department: {
              data: {
                type: 'Department',
                id,
              },
            },
          },
        },
      },
    }),
  )

  // Update all the department-dependent data after the department switch (for example the navigation sidebar and settings).
  // We cannot do this in parallel with the PATCH request, since we need to wait for the department switch to complete.
  await fetchDepartmentDependentData()

  channel.post({ id })

  return response.data
}

const currentUserQueryParams: UserCurrentQueryParams = {
  include: ['available_department_groups'],
  fields: {
    DepartmentGroup: ['name'],
  },
}

async function fetchCurrentUser(route: RouteWithParams) {
  const api = useApi()
  const provetId = route.params.provetId as string

  const userResponse = await api((client) =>
    client.GET('/{provet_id}/api/users/current', {
      params: {
        path: { provet_id: provetId },
        query: currentUserQueryParams,
      },
    }),
  )

  const included = buildIncludedResourceMap(userResponse, ['DepartmentGroup'])
  const userRelationships = buildUserRelationships(userResponse.data, included)

  user.value = userResponse.data
  departmentGroups.value = userRelationships.available_department_groups

  return userResponse.data
}

async function fetchNavigationSidebar(route: RouteWithParams) {
  const api = useApi()
  const provetId = route.params.provetId as string

  const response = await api((client) =>
    client.GET('/{provet_id}/api/navigation/sidebar', {
      params: { path: { provet_id: provetId } },
    }),
  )

  navigationSidebar.value = response.data
}

async function fetchMergedSettings(route: RouteWithParams) {
  const api = useApi()
  const provetId = route.params.provetId as string

  const mergedSettingsQueryParams: MergedSettingsQueryParams = {
    include: ['active_department', 'home_department'],
    fields: {
      Department: ['name', 'navbar_color', 'timezone', 'country'],
    },
  }

  const response = await api((client) =>
    client.GET('/{provet_id}/api/settings/merged', {
      params: {
        path: { provet_id: provetId },
        query: mergedSettingsQueryParams,
      },
    }),
  )

  const included = buildIncludedResourceMap(response, [
    'Department',
    'FeatureBlock',
  ])

  const mergedSettingsRelationships = buildMergedSettingsRelationships(
    response.data,
    included as ReturnType<typeof buildIncludedResourceMap>,
  )

  activeDepartment.value = mergedSettingsRelationships.active_department
  homeDepartment.value = mergedSettingsRelationships.home_department
  mergedSettings.value = response.data

  /**
   * Update timezone based on active department from merged settings
   */
  LuxonSettings.defaultZone =
    activeDepartment.value?.attributes?.timezone || 'UTC'

  return response.data
}

async function fetchCurrentOrganization(route: RouteWithParams) {
  const api = useApi()
  const provetId = route.params.provetId as string

  const response = await api((client) =>
    client.GET('/{provet_id}/api/organizations/current', {
      params: { path: { provet_id: provetId } },
    }),
  )

  organization.value = response.data
}

const fetchAvailableDepartments = async (
  route: RouteWithParams,
): Promise<Department[]> => {
  const api = useApi()
  const provetId = route.params.provetId as string

  const response = await api((client) =>
    client.GET('/{provet_id}/api/departments', {
      params: {
        path: { provet_id: provetId },
        query: {
          'filter[archived]': false,
          'filter[available_to_user]': true,
          'page[size]': 1000,
          fields: {
            Department: ['name', 'navbar_color'],
          },
        },
      },
    }),
  )

  departments.value = response.data
  return response.data
}

async function fetchActivePermissions(route: RouteWithParams) {
  const api = useApi()
  const provetId = route.params.provetId as string

  const response = await api((client) =>
    client.GET('/{provet_id}/api/active_permissions/current', {
      params: {
        path: { provet_id: provetId },
      },
    }),
  )

  // The response contains an array of ActivePermissionsList resources
  // We take the first one (there should only be one for current user)
  const activePermissionsList = response.data[0]
  const permissionsArray = activePermissionsList?.attributes?.permissions ?? []

  permissions.value = keyBy(permissionsArray, (p) => p.key)

  return activePermissionsList
}

async function fetchDepartmentDependentData() {
  const route = useRoute()
  // Fetch merged settings first for active department, timezone, and theme
  await fetchMergedSettings(route)

  await Promise.all([
    fetchCurrentUser(route),
    fetchAvailableDepartments(route),
    fetchNavigationSidebar(route),
    fetchCurrentOrganization(route),
    fetchActivePermissions(route),
  ])
}

function forceLogin(route: RouteLocationNormalized) {
  const nextUrl = new URL(location.href)
  const provetId = route.params.provetId as string

  const url = loginUrl({ provetId, nextUrl })
  return navigateTo(url.toString(), { external: true })
}

function getPermission(key: string): Required<Permission> {
  const permission = permissions.value?.[key]

  return {
    read: permission?.read ?? false,
    write: permission?.write ?? false,
  }
}

function getSetting(key: keyof MergedSettingsData) {
  return mergedSettings.value?.attributes?.[key] ?? undefined
}

export function useAuth() {
  const { regionName } = useDisplayNames()
  const activeDepartmentCountry = computed(() => {
    const departmentData = activeDepartment.value as Department
    return departmentData.attributes?.country
  })
  const activeDepartmentCountryName = computed(() => {
    if (!activeDepartmentCountry.value) {
      return
    }

    return regionName(activeDepartmentCountry.value)
  })

  const isLoggedIn = computed(() => !!user.value)

  const canAccessActiveDepartment = computed(() => {
    if (!activeDepartment.value) {
      return false
    }

    // Users with all_departments_active can access any department
    if (user.value?.attributes?.all_departments_active) {
      return true
    }

    // Otherwise, verify the active department is in their available departments
    return Boolean(
      activeDepartment.value?.id &&
      departments.value.some((dept) => dept.id === activeDepartment.value?.id),
    )
  })

  const departmentsListOptions = departments.value.map((department) => ({
    label: department.attributes?.name || '',
    value: department.id,
  }))
  const departmentGroupsListOptions = departmentGroups.value.map((group) => ({
    label: group.attributes?.name || '',
    value: group.id,
  }))

  const currencySettings = computed(() => {
    const { currency_code = 'EUR', currency_locale = 'en-US' } =
      mergedSettings.value?.attributes || {}

    return {
      code: currency_code as CurrencyCodes,
      locale: currency_locale,
    }
  })

  return {
    // FIXME: hacky way to tell it can't be undefined because middleware ensures it's loaded (booting you to login if not)
    // so they should always be defined. Might not be needed if we `provide` the values in the middleware instead of saving it as private module state
    user: user as Ref<User>,
    navigationSidebar,
    isLoggedIn,
    canAccessActiveDepartment,
    departments,
    departmentsListOptions,
    departmentGroups,
    departmentGroupsListOptions,
    // FIXME: hacky way to tell it can't be undefined because middleware ensures it's loaded (booting you to login if not)
    // so they should always be defined. Might not be needed if we `provide` the values in the middleware instead of saving it as private module state
    activeDepartment: activeDepartment as Ref<Department>,
    activeDepartmentCountry,
    activeDepartmentCountryName,
    organization: organization as Ref<Organization>,
    currencySettings,
    permissions,
    getPermission,
    getSetting,
    fetchCurrentUser,
    fetchAvailableDepartments,
    fetchNavigationSidebar,
    fetchMergedSettings,
    fetchCurrentOrganization,
    forceLogin,
    switchDepartment,
    homeDepartment,
    fetchActivePermissions,
    clientInteractionLanguage:
      clientInteractionLanguage.value ??
      user.value?.attributes?.language ??
      userDefaultLanguage.value,
  }
}
